import {
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControlLabel,
    Grid2 as Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { NexAlert } from "../../components/Alert/Alert"
import { NexDropzone } from "../../components/Dropzone"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { NexPageContainer } from "../../components/NexPageContainer"
import { NexTab, NexTabPanel, NexTabs } from "../../components/Tab/NexTabs"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { Label } from "../../components/Typography/Typography"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { routes } from "../../routing/NexRoutes"
import { RootContext } from "../../store/Context"
import { parseSensorFile } from "../../store/actions/MissionsActions"
import { FlightplanType } from "../../store/types/FlightPlansTypes"
import { MissionActiveFlow } from "../../store/types/MissionsTypes"
import { Types } from "../../store/types/Types"
import { getUniqueFileExtensions } from "../../utils/FileUtils"
import { MissionFlightlineErrors, validateMajorFlightplanLineErrors } from "../../utils/ValidateFlightlineEventUtils"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { MissionValidatorDialog } from "./MissionValidatorDialog"

enum MissionUploadTab {
    ACQ_FLIGHT = "ACQ_FLIGHT",
    NO_FLIGHT = "NO_FLIGHT",
    NON_ACQ_FLIGHT = "NON_ACQ_FLIGHT",
}

const getActiveFlow = (tab: MissionUploadTab, hasFile: boolean): MissionActiveFlow => {
    // Using the active flow enum to distinguish difference between the tabs and the flow that will be saved
    switch (tab) {
        case MissionUploadTab.ACQ_FLIGHT:
            if (hasFile) {
                return MissionActiveFlow.FULL_FLIGHT
            }
            return MissionActiveFlow.NO_FILE
        case MissionUploadTab.NO_FLIGHT:
            return MissionActiveFlow.NO_FLIGHT
        case MissionUploadTab.NON_ACQ_FLIGHT:
            return MissionActiveFlow.NO_ACQUISITION
        default:
            throw new Error(`Tab ${tab} is not a valid Mission Active Flow option.`)
    }
}

export const MissionUpload = () => {
    useTitle("Mission Upload")
    const nexusNavigate = useNexusNavigate()
    const projectNumber = useNexusProjectNumber()
    const { state, dispatch } = useContext(RootContext)
    const { loadingFlightplanList } = state.missions
    const appError = useAppErrorStore((state) => state.appError)
    const clearAppError = useAppErrorStore((state) => state.clear)
    const fetchEventPlannedLines = useFlightlineEventStore((state) => state.fetchEventPlannedLines)
    const isFetchingPlannedLines = useFlightlineEventStore((state) => state.isFetchingPlannedLines)
    const eventPlannedLines = useFlightlineEventStore((state) => state.eventPlannedLines)
    const allFlightplans = useFlightlineEventStore((state) => state.allFlightplans)
    const fetchAllFlightPlans = useFlightlineEventStore((state) => state.fetchAllFlightPlans)
    const resetState = useFlightlineEventStore((state) => state.resetState)
    const flightlineEventError = useFlightlineEventStore((state) => state.error)
    const clearFlightlineEventError = useFlightlineEventStore((state) => state.clearError)

    const [sensorFiles, setSensorFiles] = useState<File[]>([])
    const [loading, setLoading] = useState(false)
    const [flightlineEventErrors, setFlightlineEventErrors] = useState<MissionFlightlineErrors[] | null>(null)
    const [selectedTab, setSelectedTab] = useState<MissionUploadTab>(MissionUploadTab.ACQ_FLIGHT)
    const [fileIncluded, setFileIncluded] = useState<boolean>(true)
    const [flightplans, setFlightplans] = useState<FlightplanType[]>([])

    const activeFlow = getActiveFlow(selectedTab, fileIncluded)
    const isFileRequired = [MissionActiveFlow.FULL_FLIGHT].includes(activeFlow)
    const isFlightplanRequired = [MissionActiveFlow.FULL_FLIGHT, MissionActiveFlow.NO_FILE].includes(activeFlow)

    useEffect(() => {
        dispatch({ type: Types.Missions_ResetState })
        resetState()

        fetchAllFlightPlans(projectNumber)
    }, [])

    const handleChangeFile = (newFiles: File[]) => {
        setSensorFiles(newFiles)
    }

    const handleFlightplanChange = (selectedFlightplans: FlightplanType[]) => {
        setFlightplans(selectedFlightplans)
        fetchEventPlannedLines(
            selectedFlightplans
                .filter((flightplan) => flightplan.flight_plan_id != null)
                .map((flightplan) => flightplan.flight_plan_id!),
        )
    }

    const handleMissionFlowSelect = async () => {
        setLoading(true)
        // parse file
        if (isFileRequired) {
            const parsedMissions = await parseSensorFile(dispatch, sensorFiles, projectNumber!, eventPlannedLines)
            if (parsedMissions) {
                // validates flightlines for every parsed mission
                const majorFlightlineErrors = validateMajorFlightplanLineErrors(parsedMissions, eventPlannedLines)

                if (majorFlightlineErrors.length) {
                    // if errors are more than a certain % -> sets errors to state and shows confirmation dialog
                    setFlightlineEventErrors(majorFlightlineErrors)
                } else {
                    moveToStepper()
                }
            } else {
                setLoading(false)
            }
        } else {
            moveToStepper()
        }
    }

    const closeModal = () => {
        setFlightlineEventErrors(null)
        setLoading(false)
    }

    const moveToStepper = () => {
        closeModal()
        dispatch({
            type: Types.Missions_SelectActiveFlow,
            payload: { activeFlow },
        })
        // go to mission editor
        nexusNavigate.submitNewMission(
            projectNumber,
            flightplans
                .filter((flightplan) => flightplan.flight_plan_id != null)
                .map((flightplan) => flightplan.flight_plan_id!),
        )
    }

    return (
        <NexPageContainer maxWidth="md">
            <ProjectTitle projectNumber={projectNumber} />

            <Paper sx={{ mb: 2 }}>
                <NexTabs
                    variant={"fullWidth"}
                    indicatorColor="secondary"
                    value={selectedTab}
                    onChange={(_, value) => setSelectedTab(value)}
                >
                    <NexTab label={"Acquisition Flight"} value={MissionUploadTab.ACQ_FLIGHT} />
                    <NexTab label={"Non-Acquisition Flight"} value={MissionUploadTab.NON_ACQ_FLIGHT} />
                    <NexTab label={"No Flight Today"} value={MissionUploadTab.NO_FLIGHT} />
                </NexTabs>

                <NexTabPanel index={MissionUploadTab.ACQ_FLIGHT} value={selectedTab}>
                    <Stack rowGap={2}>
                        <Label disableGutter={true}>Select the Flightplans Used</Label>
                        <NexAutocomplete
                            multiple={true}
                            value={flightplans}
                            options={allFlightplans}
                            onChange={(_, value) => handleFlightplanChange(value)}
                            getOptionDisabled={(option: FlightplanType) => option.status !== "Active"}
                            getOptionLabel={(opt: FlightplanType) =>
                                opt.flight_plan_name + (opt.status === "Active" ? "" : ` (${opt.status})`)
                            }
                            TextFieldProps={{ label: "Select", required: isFlightplanRequired }}
                            loading={loadingFlightplanList || isFetchingPlannedLines}
                            width="lg"
                        />
                        <FormControlLabel
                            label={"Sensor File Included"}
                            control={
                                <Checkbox
                                    checked={fileIncluded}
                                    onChange={(_, checked) => {
                                        setFileIncluded(checked)
                                    }}
                                />
                            }
                        />

                        <Collapse in={fileIncluded} sx={{ pb: fileIncluded ? 2 : 0 }}>
                            <Label gutterBottom="sm">Sensor File Upload </Label>
                            <NexDropzone
                                acceptedFileTypes={[".rpp", ".zip", ".txt", ".tfx", ".xml"]}
                                filesLimit={9}
                                files={sensorFiles}
                                onChange={handleChangeFile}
                                requireSameFileType={true}
                            />
                        </Collapse>
                    </Stack>
                </NexTabPanel>
                <NexTabPanel index={MissionUploadTab.NON_ACQ_FLIGHT} value={selectedTab}>
                    No additional action needed for Non-Acquisition Flight
                </NexTabPanel>
                <NexTabPanel index={MissionUploadTab.NO_FLIGHT} value={selectedTab}>
                    No additional action needed for No Flight Today
                </NexTabPanel>

                <Grid sx={{ p: 2 }}>
                    {/* Error Alerts */}
                    {(appError || flightlineEventError) && (
                        <NexAlert
                            sx={{ mb: 2 }}
                            severity="error"
                            animate={true}
                            onClose={() => {
                                clearAppError()
                                clearFlightlineEventError()
                            }}
                        >
                            <Typography variant="body2">{appError}</Typography>
                            <Typography variant="body2">{flightlineEventError}</Typography>
                        </NexAlert>
                    )}

                    {/* Back and Continue Buttons */}
                    <Grid container justifyContent="space-between" spacing={2}>
                        <Grid>
                            <Button component={Link} to={routes.home} variant="outlined" color="primary" size="large">
                                Back
                            </Button>
                        </Grid>
                        <Grid>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid>
                                    {/* Upload Button */}
                                    <LoadingButton
                                        pending={loading}
                                        size="large"
                                        color="primary"
                                        variant="contained"
                                        onClick={handleMissionFlowSelect}
                                        disabled={
                                            (isFileRequired && sensorFiles.length === 0) ||
                                            getUniqueFileExtensions(sensorFiles).length > 1 ||
                                            (isFlightplanRequired && flightplans.length === 0) ||
                                            isFetchingPlannedLines ||
                                            loading
                                        }
                                    >
                                        Continue
                                    </LoadingButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Validation Dialog */}
            <Dialog fullWidth open={!!flightlineEventErrors} onClose={closeModal}>
                {flightlineEventErrors && <MissionValidatorDialog flightlineEventErrors={flightlineEventErrors} />}
                <DialogContent sx={{ overflowY: "initial" }}>
                    <DialogContentText variant={"h6"}>Do you wish to proceed?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button color={"primary"} onClick={moveToStepper}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </NexPageContainer>
    )
}
